// Detail d'un outil du navigateur (browser_open, browser_read…) : le bloc ressource qui porte le
// jeton n'est JAMAIS montre brut ; a sa place, une carte « Navigateur » avec « Voir le
// navigateur », qui ouvre (ou rouvre) le panneau en direct sur la session de ce resultat.
import { ActionDetailsWrapper } from "@app/components/actions/ActionDetailsWrapper";
import type { ActionDetailsDisplayContext } from "@app/components/actions/mcp/details/types";
import { useConversationSidePanelContext } from "@app/components/assistant/conversation/ConversationSidePanelContext";
import { useActiveConversationId } from "@app/hooks/useActiveConversationId";
import { getOutputText } from "@app/lib/actions/mcp_internal_actions/output_schemas";
import { formaterAdresse } from "@app/lib/compta/navigateur/adresse";
import {
  gesteDeLOutil,
  LIBELLES_NAVIGATEUR as L,
  libelleGeste,
} from "@app/lib/compta/navigateur/libelles";
import { registreNavigateur } from "@app/lib/compta/navigateur/ouverture";
import {
  extraireSessionNavigateur,
  sansBlocNavigateur,
} from "@app/lib/compta/navigateur/session";
import type { AgentMCPActionWithOutputType } from "@app/types/actions";
import { NAVIGATEUR_SIDE_PANEL_TYPE } from "@app/types/conversation_side_panel";
import {
  Button,
  ContentMessage,
  Globe01,
  Icon,
  Markdown,
} from "@dust-tt/sparkle";

export function CarteNavigateur({
  action,
}: {
  action: Pick<AgentMCPActionWithOutputType, "sId" | "createdAt" | "output">;
}) {
  const { openPanel } = useConversationSidePanelContext();
  const conversationId = useActiveConversationId();
  const session = extraireSessionNavigateur(action.output);
  if (!session) {
    return null;
  }
  const page = session.url ? formaterAdresse(session.url) : null;

  return (
    <div
      className="flex items-center gap-3 rounded-2xl border border-border bg-background p-3 dark:border-border-night dark:bg-background-night"
      data-testid="carte-navigateur"
    >
      <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-muted-background dark:bg-muted-background-night">
        <Icon
          visual={Globe01}
          size="xs"
          className="text-foreground dark:text-foreground-night"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-sm font-semibold text-foreground dark:text-foreground-night">
          {L.titre}
        </span>
        <span className="truncate text-xs text-muted-foreground dark:text-muted-foreground-night">
          {session.titre ??
            (page ? `${page.hote}${page.chemin}` : L.carteDescription)}
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        label={L.voirLeNavigateur}
        onClick={() => {
          if (conversationId) {
            registreNavigateur.enregistrer(conversationId, {
              session,
              actionId: action.sId,
              horodatage: action.createdAt,
            });
          }
          openPanel({ type: NAVIGATEUR_SIDE_PANEL_TYPE });
        }}
      />
    </div>
  );
}

export function NavigateurActionDetails({
  action,
  displayContext,
}: {
  action: AgentMCPActionWithOutputType;
  displayContext: ActionDetailsDisplayContext;
}) {
  const geste = gesteDeLOutil(action.toolName) ?? "work";
  const url = typeof action.params.url === "string" ? action.params.url : null;
  const textes = sansBlocNavigateur(action.output ?? [])
    .map(getOutputText)
    .filter((t) => t.trim() !== "");

  return (
    <ActionDetailsWrapper
      displayContext={displayContext}
      actionName={`${L.titre} : ${libelleGeste(geste, url)}`}
      visual={Globe01}
    >
      <div className="flex flex-col gap-4 py-4 pl-6">
        <CarteNavigateur action={action} />
        {displayContext !== "conversation" &&
          textes.map((texte, i) => (
            <ContentMessage key={i} variant="primary" size="lg">
              <Markdown content={texte} />
            </ContentMessage>
          ))}
      </div>
    </ActionDetailsWrapper>
  );
}
